import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../../../layout";
import { faArrowLeft, faExclamationCircle, faEye, faPen } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import MISButton from "../../../../components/button";
import { Card, Col, Row, Spinner, Stack } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServiceManager } from "../../../../services/ServiceManager";
import Skeleton from "react-loading-skeleton";
import IExtractClient from "../../../../models/ExtractClient";
import { APIStatusCode, MisType, Permissions } from "../../../../app/enums";
import { format } from "date-fns";
import { GUID } from "../../../../app/common/helper/Guid";
import CustomModal from "../../../../components/modal";
import IconElement from "../../../../components/icon";
import ExtractClientUserTypes from "./user-types/extract-client-user-types";
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import MISTooltip from "../../../../components/tooltip";
import { PERMISSION_REQUIRED } from "../../../../app/constants";
import ExtractClientUpsert from "./extract-client-upsert";
import ArborProviderConfigView from "../../../../components/misconfig/arbor/arbor-providerconfig-view";
import BromcomProviderConfigView from "../../../../components/misconfig/bromcom/bromcom-providerconfig-view";

export default function ExtractClientDetails() {
    const [revealCredentials, setRevealCredentials] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [clientId, setClientId] = useState<GUID | null>(null);
    const [updateExtractClient, setUpdateExtractClient] = useState(false);
    const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);

    const handleCloseResetModal = () => setShowResetModal(false);

    let navigate = useNavigate();
    const { establishmentId, configurationId } = useParams();

    const { isLoading, data } = useQuery<IExtractClient>({
        queryKey: [`extract-client-details-${establishmentId}-${configurationId}`],
        queryFn: () =>
            ServiceManager.ConfigurationService.GetConfigurationsById(establishmentId, configurationId),
        refetchOnWindowFocus: false,
    });


    const resetClientSecret = (clientId: GUID) => {
        setClientId(clientId);
        setShowResetModal(true);
    }

    const onClickUpdateExtractClient = (isTrue: boolean) => {
        setUpdateExtractClient(isTrue);
    }

    const ResetClientSecretModal = () => {
        const queryClient = useQueryClient();
        const mutation = useMutation({
            mutationFn: () => {
                return ServiceManager.ConfigurationService.ResetClientSecret(establishmentId, clientId);
            },
            onError: (error, variables, context) => {

            },
            onSuccess: (data, variables, context) => {
                if (data === null) {
                    ServiceManager.ToastService.showError("Unable to reset client secret.");
                }
                else {
                    if (data.status === APIStatusCode.NoContent) {
                        ServiceManager.ToastService.showSuccess("Client secret reset successfully.");
                        queryClient.invalidateQueries({ queryKey: [`extract-client-details-${establishmentId}-${configurationId}`] });
                        handleCloseResetModal();
                    }
                    else {
                        let errorMessage = (data.response && data.response.data && data.response.data.errors.errors) ? data.response.data.errors.errors : data.message;
                        ServiceManager.ToastService.showError(errorMessage.toString());
                    }
                }
            }
        });

        return (
            <Stack>
                <p className="heading text-center">Reset Client Secret</p>
                <p className="text-center">Are you sure you wish to generate and apply a new Client Secret?</p>
                {
                    !mutation.isPending
                        ? (<Stack direction="horizontal" className="mt-3">
                            <MISButton text="Cancel" className="popup-btn right-margin10 btn btn-outline-secondary w-50" FnOnClick={handleCloseResetModal} />
                            <MISButton text="Confirm" className="popup-btn right-margin10 btn btn-primary crbc-bg-color w-50" FnOnClick={() => { mutation.mutate(); }} />
                        </Stack>
                        ) : (
                            <div className="d-flex justify-content-center mt-3">
                                <Spinner animation="border" variant="info" role="status"></Spinner>
                            </div>
                        )
                }
            </Stack>
        )
    }

    return (
        <>
            {updateExtractClient === false ?
                (
                    <Layout title="MIS Client Details"
                        button={<>
                            <MISButton text="Configure Custom Fields" className="btn btn-primary crbc-bg-color me-2" FnOnClick={() => navigate(`configure-custom-fields/${data?.clientID}`)} disabled={!data?.clientID}></MISButton>
                            <MISButton text="Transform" className="btn btn-primary crbc-bg-color me-2" FnOnClick={() => navigate(`transform/${data?.clientID}`)} disabled={!data?.clientID}></MISButton>
                            <MISButton text="Overrides" className="btn btn-secondary" FnOnClick={() => navigate('overrides')}></MISButton></>}
                        backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={() => navigate(-1)} className="me-4 fs-5 cursor-pointer" />}
                    >
                        <div className="auto-height-calc">                            
                            <div className="stats-widget-box" style={{ height: 'auto' }}>
                                <Stack direction="horizontal">
                                    <div className="stats-widget-box-header">
                                        Client Details
                                    </div>
                                    {
                                        canEditEstablishments ?
                                            <MISButton text="Edit" className="btn btn-outline-secondary ms-auto" FnOnClick={() => onClickUpdateExtractClient(true)} icon={<FontAwesomeIcon icon={faPen} />}></MISButton>
                                            :
                                            <MISTooltip message={PERMISSION_REQUIRED} placement="top">
                                                <div className="disabled ms-auto">
                                                    <MISButton text="Edit" className="btn btn-secondary" FnOnClick={() => null} disabled icon={<FontAwesomeIcon icon={faPen} />}></MISButton>
                                                </div>
                                            </MISTooltip>
                                    }
                                </Stack>
                                <Stack>
                                    {!isLoading && data != null &&
                                        <Row>
                                            <Col xl={6} sm={5} className="text-break">
                                                <Row>
                                                    <Col md={4} className="pb-2"><b>Establishment</b></Col>
                                                    <Col md={8}>{data.establishmentName}</Col>
                                                </Row>
                                                {data.misType !== MisType.Bromcom && (
                                                    <Row>
                                                        <Col md={4} className="pb-2"><b>MIS</b></Col>
                                                        <Col md={8}>{MisType[data.misType]?.toUpperCase()}</Col>
                                                    </Row>
                                                )}
                                                {data.misType === MisType.Sims &&
                                                    <Row>
                                                        <Col md={4} className="pb-2"><b>SIMS Path</b></Col>
                                                        <Col md={8}>{JSON.parse(data.clientConfiguration?.settingsJson || '{}').SimsPath}</Col>
                                                    </Row>
                                                }
                                                {data.misType === MisType.Arbor &&
                                                    <ArborProviderConfigView configJson={data.clientConfiguration?.settingsJson || ''} arborApplicationID={data.clientConfiguration?.arborApplicationID} />
                                                }                                           
                                                {data.misType === MisType.Bromcom &&
                                                    <BromcomProviderConfigView configJson={data.clientConfiguration?.settingsJson || ''} />
                                                }

                                                <Row>
                                                    <Col md={4} className="pb-2"><b>Client Start Date</b></Col>
                                                    <Col md={8}>{data.clientConfiguration?.dateCreated ? format(new Date(data.clientConfiguration?.dateCreated), "dd/MM/yyyy") : ''}</Col>
                                                </Row>
                                            </Col>
                                            <Col xl={4} sm={7}>
                                                <div className="client-credentials">
                                                    <Card style={{ height: 'auto' }}>
                                                        <Card.Body className="d-flex flex-column justify-content-between">
                                                            <Stack direction="horizontal">
                                                                <Card.Title as="h6">Credentials</Card.Title>
                                                                {data.misType === MisType.Sims ?
                                                                    canEditEstablishments ?
                                                                        <span className="ms-auto crbc-color fw-bold cursor-pointer" onClick={() => resetClientSecret(data.clientID)}>Reset Client Secret</span>
                                                                        :
                                                                        <MISTooltip message={PERMISSION_REQUIRED} placement="top">
                                                                            <div className="disabled ms-auto">
                                                                                <span className="ms-auto fw-bold" onClick={() => null}>Reset Client Secret</span>
                                                                            </div>
                                                                        </MISTooltip>
                                                                    : <></>
                                                                }
                                                            </Stack>

                                                            {!revealCredentials &&
                                                                <MISButton text="Reveal" className="btn btn-light w-100"
                                                                    FnOnClick={() => setRevealCredentials(true)} icon={<FontAwesomeIcon icon={faEye} />}></MISButton>}

                                                            {revealCredentials &&
                                                                <div className="container">
                                                                    <div className="row my-2">
                                                                        <div className="ps-0 col-3 font-11">Client ID</div>
                                                                        <div className="ps-0 col-9 font-11">{data.clientID}</div>
                                                                    </div>
                                                                    {data.misType === MisType.Sims && <>
                                                                        <div className="row">
                                                                            <div className="ps-0 col-3 font-11">Client Secret</div>
                                                                            <div className="ps-0 col-9 font-11">{data.secretKey}</div>
                                                                        </div>
                                                                        <div className="row">
                                                                            <div className="ps-0 col-3 font-11">Username</div>
                                                                            <div className="ps-0 col-9 font-11">{JSON.parse(data.clientConfiguration?.settingsJson || '{}').Username}</div>
                                                                        </div>
                                                                        <div className="row">
                                                                            <div className="ps-0 col-3 font-11">Password</div>
                                                                            <div className="ps-0 col-9 font-11">{JSON.parse(data.clientConfiguration?.settingsJson || '{}').Password}</div>
                                                                        </div>
                                                                    </>}
                                                                </div>
                                                            }
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            </Col>
                                            <Col md={2}></Col>
                                        </Row>
                                    }
                                    {isLoading &&
                                        <Row>
                                            <Col md={5}><Skeleton height={20} count={4} /></Col>
                                            <Col md={5}><Skeleton height={20} count={4} /></Col>
                                            <Col md={2}></Col>
                                        </Row>
                                    }
                                </Stack>
                            </div>

                            <ExtractClientUserTypes />
                        </div>
                        
                        <CustomModal isShow={showResetModal} handleClose={handleCloseResetModal} header={<IconElement iconType={faExclamationCircle} headerClass="orange-icon ms-6" color="#D92D20" />} size="md" >
                            <ResetClientSecretModal />
                        </CustomModal>

                    </Layout>
                ) :
                (
                    <ExtractClientUpsert extractClient={data!} upsertExtractClient={onClickUpdateExtractClient} />
                )
            }
        </>
    );
}